import type { ReportSeverity, ReportSeverityKey } from '@ipfabric/utils/report';
import { isNullableOrEmptyString } from '@ipfabric/utils/types/isNullableOrEmptyString';
import classNames from 'classnames';
import type { FC, HTMLAttributes } from 'react';
import {
  getAccessibleTabIndex,
  getAriaButtonRoleAttribute,
  handleAccessibleButtonKeyDown,
} from '../Button/ariaHelpers';
import { Icon } from '../Icon/Icon';
import type { IconGlyph } from '../Icon/IconGlyph';
import { useConditionalClickHandler } from '../hooks/useConditionalClickHandler';

type BadgeColor =
  | 'info'
  | 'error'
  | 'notification'
  | 'warning'
  | 'success'
  | 'neutral';

export type BadgeProps = HTMLAttributes<HTMLDivElement> & {
  color: BadgeColor;
  variant?: 'dot' | 'plain';
  icon?: IconGlyph;
  faded?: boolean;
  interactive?: boolean;
};

const colorClassName = {
  info: 'bg-status-info text-status-on-info',
  error: 'bg-status-error text-status-on-error',
  notification: 'bg-status-error text-status-on-error',
  warning: 'bg-status-warning text-status-on-warning',
  success: 'bg-status-success text-status-on-success',
  neutral: 'bg-shade-10 text-default',
};

const variantClassName = {
  dot: 'size-3 rounded-full',
  plain: 'min-w-4 rounded-sm',
};

export const Badge: FC<BadgeProps> = ({
  className,
  color,
  variant = 'plain',
  icon,
  faded,
  children,
  interactive,
  onClick,
  ...restProps
}) => {
  const hasText = !isNullableOrEmptyString(children);
  const hasIcon = !!icon;

  const handleClick = useConditionalClickHandler<HTMLDivElement>(
    !interactive,
    onClick,
  );

  return (
    <div
      {...restProps}
      className={classNames(
        'inline-flex flex-row items-center justify-center',
        'whitespace-nowrap text-xs font-semibold',
        variantClassName[variant],
        colorClassName[color],
        faded && 'bg-state-faded',
        interactive &&
          'hover:bg-state-hover active:bg-state-press cursor-pointer',
        hasText && hasIcon && 'gap-x-1',
        hasText && 'px-3xs',
        className,
      )}
      onClick={handleClick}
      {...getAriaButtonRoleAttribute(interactive)}
      tabIndex={getAccessibleTabIndex(!interactive)}
      onKeyDown={(e) => {
        handleAccessibleButtonKeyDown(e, !interactive);
      }}
    >
      {variant === 'plain' && hasIcon && (
        <Icon size='2xs' glyph={icon} autoWidth={hasText} />
      )}
      {variant === 'plain' && children}
    </div>
  );
};

export const severityToBadgeColor: {
  [Key in ReportSeverity | ReportSeverityKey]: BadgeColor;
} = {
  '0': 'success',
  '10': 'info',
  '20': 'warning',
  '30': 'error',
};
